// =============================================================================
// NewBookingModal — admin walk-in / phone booking creator (multi-pick)
// =============================================================================
// Two modes:
//
// QUICK (opened from a calendar + cell: date, time and location are known) —
// ONE screen: a chip header (datum · tijd · experience · locatie), each chip
// tappable to edit that value in place; party + price; the Klant block; the
// Betaling block; one confirm button. The experience defaults to the
// location's most-booked one over the trailing 30 days (refdata
// experience_ranking) when the cell didn't pin it. "+ Meer expedities" bails
// out to the wizard with the current pick committed to the cart.
//
// WIZARD (menu "Nieuwe boeking", no prefill) — the original three steps:
//   1. Slot     — picks one experience-pick at a time; cart chips on top;
//                  "Voeg toe en nog één" (stay) or "Volgende" (advance).
//   2. Klant    — name / email / phone / locale.
//   3. Betaling — payment method, aggregate amount, notes, over-capacity ack.
//
// On confirm: POSTs the multi-departure shape to booking-walkin-create.
// Success shows the first booking_number + per-departure email status; closing
// refreshes the calendar.
// =============================================================================
import { useCallback, useEffect, useMemo, useRef, useState, type Ref } from 'react';
import { todayVenue } from '../lib/dates';
import { formatError } from '../lib/errors';
import {
  fetchWalkinRefData,
  fetchSlotsForDay,
  resolveStartTimeId,
  suggestPriceCents,
  type BookingLocation,
  type BookingExperience,
} from '../lib/walkin-data';
import { useSingleRoomLocations } from '../lib/room-counts';
import type { Departure } from '../lib/departures';
import {
  createWalkinBooking,
  type WalkinPaymentMethod,
  type WalkinDepartureInput,
  type WalkinDepartureResult,
} from '../api/walkin';
import { validateDiscountCodeCart } from '../lib/vouchers';
import { TerminalChargePanel, type TerminalChargeHandle } from './TerminalChargePanel';
import { GiftCardApplyPanel } from './GiftCardApplyPanel';
import { resendBookingEmail } from '../api/resend-email';
import { checkinBookingById } from '../api/checkin';
import { useAuth } from '../lib/auth';
import {
  readerProblemText,
  readerUnusable,
  terminalReaderStatus,
  type TerminalReaderState,
} from '../api/terminal';

type Step = 'slot' | 'customer' | 'payment';

// One operator-built pick in the cart. Carries the slot + party + price the
// RPC needs, plus the human-readable bits (experience/location/room) for UI.
interface CartPick {
  // Display
  experience: BookingExperience;
  location:   BookingLocation;
  slot:       Departure;       // has room/time/booked/capacity/etc
  // Submit
  partySize:  number;
  priceCents: number;
}

interface Props {
  onClose: (created: boolean) => void;
  // When opened from a calendar cell, pre-fill the date and (optionally) the
  // slot time. Once experience+location are picked and the slot list loads,
  // the row matching prefillTime auto-selects so the operator only types
  // the customer.
  prefillDate?: string;
  prefillTime?: string;
  // Set when clicking an empty Departure cell — we know the cell's
  // experience and location, so jump directly to the slot picker.
  prefillExperienceId?: string;
  prefillLocationId?: string;
}

// UI-level payment choice. 'pos' is not a server payment_method: it creates
// the booking as 'unpaid' (no email yet) and immediately charges it on the
// location's WisePOS via booking-terminal; the Stripe webhook settles it and
// sends the ticket. Offered only when the cart's location has a reader.
// 'giftcard' is a UI choice too: the booking is created 'unpaid' (no mail
// yet), then a physical gift card is spent on it (GiftCardApplyPanel) and any
// rest goes to the WisePOS like 'pos'.
type UiPaymentMethod = WalkinPaymentMethod | 'pos' | 'giftcard';

const PAYMENT_LABEL: Record<UiPaymentMethod, string> = {
  pos: 'Pinterminal (POS)',
  giftcard: 'Cadeaubon (rest op pinterminal)',
  cash: 'Contant',
  card_terminal: 'Pinterminal (handmatig geregistreerd)',
  unpaid: 'Nog niet betaald',
  comp: 'Comp / gratis',
};

const LOCALES: Array<{ code: string; label: string }> = [
  { code: 'nl', label: 'NL' },
  { code: 'en', label: 'EN' },
];

// Money input as a PLAIN text box: type="number" + a controlled toFixed(2)
// value reformats on every keystroke (typing "3" instantly becomes "3.00")
// and adds spinner arrows — unusable on the counter phone. This keeps a free
// local draft while typing, accepts "35", "35,5" and "35.50", pushes cents up
// as soon as the draft parses, and formats only on blur.
function MoneyInput({
  cents,
  disabled = false,
  onCents,
}: {
  cents: number;
  disabled?: boolean;
  onCents: (c: number) => void;
}) {
  const [text, setText] = useState<string>((cents / 100).toFixed(2));
  const lastExternalRef = useRef(cents);
  useEffect(() => {
    if (cents !== lastExternalRef.current) {
      lastExternalRef.current = cents;
      setText((cents / 100).toFixed(2));
    }
  }, [cents]);
  const parse = (v: string): number | null => {
    const norm = v.trim().replace(',', '.').replace(/[^0-9.]/g, '');
    if (norm === '' || norm.split('.').length > 2) return null;
    const n = Number(norm);
    if (!Number.isFinite(n) || n < 0) return null;
    return Math.round(n * 100);
  };
  const invalid = !disabled && parse(text) === null;
  return (
    <>
      <input
        type="text"
        inputMode="decimal"
        autoComplete="off"
        className={invalid ? 'walkin-money-invalid' : undefined}
        disabled={disabled}
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          const c = parse(e.target.value);
          if (c !== null) {
            lastExternalRef.current = c;
            onCents(c);
          }
        }}
        onBlur={() => {
          const c = parse(text) ?? lastExternalRef.current;
          lastExternalRef.current = c;
          onCents(c);
          setText((c / 100).toFixed(2));
        }}
      />
      {invalid ? (
        <span className="walkin-discount-bad">Geen geldig bedrag.</span>
      ) : null}
    </>
  );
}

// Chip label for a YYYY-MM-DD date. Noon anchor sidesteps the UTC-midnight
// day-shift a bare Date('YYYY-MM-DD') parse would give west of Greenwich.
function fmtChipDate(date: string): string {
  const d = new Date(`${date}T12:00:00`);
  return d.toLocaleDateString('nl-NL', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

export function NewBookingModal({
  onClose,
  prefillDate,
  prefillTime,
  prefillExperienceId,
  prefillLocationId,
}: Props) {
  const [step, setStep] = useState<Step>('slot');
  // Quick mode when the calendar cell pinned date + time + location; the
  // wizard stays for the prefill-less "Nieuwe boeking" button. Quick can
  // hand off to the wizard ("+ Meer expedities") but never the other way.
  const [mode, setMode] = useState<'quick' | 'wizard'>(
    prefillDate && prefillTime && prefillLocationId ? 'quick' : 'wizard',
  );
  // Which quick-mode chip is being edited (null = all collapsed). 'pick' is
  // the combined experience + party panel — the two values every booking
  // needs a decision on — reachable from BOTH chips and open by default.
  const [editing, setEditing] = useState<
    'date' | 'time' | 'pick' | 'location' | 'price' | null
  >(prefillDate && prefillTime && prefillLocationId ? 'pick' : null);
  // Quick-mode payment route: POS first; betaallink is the fallback when the
  // reader can't take the payment (requires an e-mail). Cash no longer
  // exists at the counter; comp / handmatige pin live only in the wizard.
  const [payVia, setPayVia] = useState<'pos' | 'link'>('pos');
  const [payViaTouched, setPayViaTouched] = useState(false);
  // Quick-mode reveals for the rare inputs.
  const [showVoucher, setShowVoucher] = useState(false);
  const [showNote, setShowNote] = useState(false);

  // Reference data (locations + experiences). Shared across all picks.
  const [locations, setLocations] = useState<BookingLocation[]>([]);
  const [experiences, setExperiences] = useState<BookingExperience[]>([]);
  // location_id → experience ids, most-booked first (trailing 30 days).
  const [experienceRanking, setExperienceRanking] = useState<Record<string, string[]>>({});
  const [refLoading, setRefLoading] = useState(true);
  const [refError, setRefError] = useState<string | null>(null);

  // Cart of picks the operator has already confirmed.
  const [cart, setCart] = useState<CartPick[]>([]);

  // ---- Slot-builder draft (in-progress pick) ------------------------------
  const [locationId, setLocationId] = useState<string>(prefillLocationId ?? '');
  const [experienceId, setExperienceId] = useState<string>(prefillExperienceId ?? '');
  const [date, setDate] = useState<string>(() => prefillDate ?? todayVenue());
  const [prefillActive, setPrefillActive] = useState<boolean>(prefillTime != null);

  const [slots, setSlots] = useState<Departure[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState<string | null>(null);
  const [pickedSlot, setPickedSlot] = useState<Departure | null>(null);
  const [partySize, setPartySize] = useState<number>(2);
  const [priceCents, setPriceCents] = useState<number>(0);
  const [priceTouched, setPriceTouched] = useState(false);

  // ---- Customer step ------------------------------------------------------
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [locale, setLocale] = useState<string>('nl');
  // "Direct inchecken": the visitor is at the counter. Pre-checked when the
  // earliest slot in the cart starts within 40 minutes (or already started);
  // the operator can flip it either way. Checked ⇒ e-mail optional (no e-mail
  // = anonymous booking, name on the booking itself, no ticket mail).
  const [checkin, setCheckin] = useState(false);
  const [checkinTouched, setCheckinTouched] = useState(false);

  // ---- Payment step -------------------------------------------------------
  const [paymentMethod, setPaymentMethod] = useState<UiPaymentMethod>('cash');
  const { can } = useAuth();
  const canSellGiftCards = can('booking_gift_card_sell');
  // True once the operator explicitly picked a method — before that, the
  // default follows the cart's location (POS when it has a reader).
  const [paymentTouched, setPaymentTouched] = useState(false);
  // Betaallink in de bevestigingsmail (alleen bij 'unpaid') — standaard aan.
  const [sendPaymentLink, setSendPaymentLink] = useState(true);
  const [amountTouched, setAmountTouched] = useState(false);
  const [amountCents, setAmountCents] = useState<number>(0);
  const [overCapacityAck, setOverCapacityAck] = useState(false);
  const [notes, setNotes] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  const [discountPreview, setDiscountPreview] = useState<{
    valid: boolean;
    amountOffCents?: number;
    reason?: string;
  } | null>(null);
  const [discountChecking, setDiscountChecking] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Success state -----------------------------------------------------------
  const [success, setSuccess] = useState<{
    booking_id: string;
    booking_number: string;
    departures: WalkinDepartureResult[];
    // POS: the terminal charge runs inside the success view.
    pos: boolean;
    // Cadeaubon: the card is spent first inside the success view.
    giftCard: boolean;
    // "Direct inchecken" was on (POS: check-in follows the payment).
    checkin: boolean;
    anonymous: boolean;
  } | null>(null);

  // ---- Initial load: locations + experiences ------------------------------
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRefLoading(true);
      setRefError(null);
      try {
        const ref = await fetchWalkinRefData();
        if (cancelled) return;
        setLocations(ref.locations);
        setExperiences(ref.experiences);
        setExperienceRanking(ref.experienceRanking);
        if (!prefillLocationId && ref.locations.length === 1) {
          setLocationId(ref.locations[0]!.id);
        }
        if (!prefillExperienceId && ref.experiences.length === 1) {
          setExperienceId(ref.experiences[0]!.id);
        }
      } catch (e) {
        if (!cancelled) setRefError(formatError(e));
      } finally {
        if (!cancelled) setRefLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ---- Popularity default -------------------------------------------------
  // No experience chosen yet but the location is known: default to the
  // location's most-booked experience of the trailing 30 days. Never
  // overrides an operator's own pick (only fires while experienceId is '').
  useEffect(() => {
    if (experienceId || !locationId || experiences.length === 0) return;
    const ranked = (experienceRanking[locationId] ?? []).find((id) =>
      experiences.some((e) => e.id === id),
    );
    if (ranked) setExperienceId(ranked);
  }, [experienceId, locationId, experiences, experienceRanking]);

  // ---- Slot list refresh whenever the picker key changes ------------------
  useEffect(() => {
    if (!locationId || !experienceId || !date) {
      setSlots([]);
      return;
    }
    let cancelled = false;
    setSlotsLoading(true);
    setSlotsError(null);
    const prevPickedTime = pickedSlot?.time_of_day ?? null;
    (async () => {
      try {
        const rows = await fetchSlotsForDay(date, experienceId, locationId);
        if (cancelled) return;
        rows.sort((a, b) => a.slot_start.localeCompare(b.slot_start));
        setSlots(rows);
        const anchorTime = prefillActive && prefillTime ? prefillTime : prevPickedTime;
        const match = anchorTime ? rows.find((r) => r.time_of_day === anchorTime) : undefined;
        setPickedSlot(match ?? null);
        if (prefillActive) setPrefillActive(false);
      } catch (e) {
        if (!cancelled) setSlotsError(formatError(e));
      } finally {
        if (!cancelled) setSlotsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId, experienceId, date]);

  // ---- Suggested price for the draft pick ---------------------------------
  useEffect(() => {
    if (!pickedSlot || priceTouched) return;
    let cancelled = false;
    (async () => {
      try {
        const cents = await suggestPriceCents(
          pickedSlot.experience_id,
          pickedSlot.location_id,
          pickedSlot.slot_start,
          partySize,
        );
        if (!cancelled) setPriceCents(cents);
      } catch {
        /* fall back: operator types manually */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pickedSlot, partySize, priceTouched]);

  // ---- Effective picks ----------------------------------------------------
  // Quick mode never commits its single pick to the cart; it lives as a
  // "draft pick" derived from the current selection and joins the cart
  // virtually for every total / validation / submit computation. In wizard
  // mode allPicks === cart (the wizard commits drafts on advance).
  const draftPick = useMemo<CartPick | null>(() => {
    if (!pickedSlot) return null;
    const experience = experiences.find((e) => e.id === experienceId);
    const location = locations.find((l) => l.id === locationId);
    if (!experience || !location) return null;
    return { experience, location, slot: pickedSlot, partySize, priceCents };
  }, [pickedSlot, experiences, locations, experienceId, locationId, partySize, priceCents]);

  const allPicks = useMemo<CartPick[]>(
    () => (mode === 'quick' && draftPick ? [...cart, draftPick] : cart),
    [mode, cart, draftPick],
  );

  // ---- Cart-derived totals ------------------------------------------------
  const subtotalCents = useMemo(
    () => allPicks.reduce((sum, p) => sum + p.priceCents, 0),
    [allPicks],
  );
  const totalPartySize = useMemo(
    () => allPicks.reduce((sum, p) => sum + p.partySize, 0),
    [allPicks],
  );

  // Cumulative pending claims by (room_id, slot_start) across the cart. The
  // slot picker adds these to each slot's `booked` count so the operator
  // sees realistic remaining capacity after the picks already in the cart.
  // The RPC's cumulative-capacity check is the security backstop; this is
  // the UX layer that surfaces it before submit.
  const cartClaims = useMemo(() => {
    const claims = new Map<string, number>();
    for (const p of cart) {
      const key = `${p.slot.room_id}|${p.slot.slot_start}`;
      claims.set(key, (claims.get(key) ?? 0) + p.partySize);
    }
    return claims;
  }, [cart]);

  // Claims INCLUDING the quick-mode draft pick — used for over-capacity
  // validation only. The slot lists keep showing cartClaims (committed picks)
  // so the draft doesn't inflate its own slot's count while choosing.
  const allClaims = useMemo(() => {
    const claims = new Map<string, number>();
    for (const p of allPicks) {
      const key = `${p.slot.room_id}|${p.slot.slot_start}`;
      claims.set(key, (claims.get(key) ?? 0) + p.partySize);
    }
    return claims;
  }, [allPicks]);

  // Earliest departure in the cart drives the "Direct inchecken" default:
  // within 40 minutes (negative = already started) means the visitor is
  // standing here. ISO instants compare correctly regardless of zone.
  const earliestCartStartMs = useMemo(() => {
    let min: number | null = null;
    for (const p of allPicks) {
      const ms = Date.parse(p.slot.slot_start);
      if (Number.isFinite(ms) && (min === null || ms < min)) min = ms;
    }
    return min;
  }, [allPicks]);
  const CHECKIN_WINDOW_MS = 40 * 60 * 1000;
  const checkinSuggested =
    earliestCartStartMs !== null && earliestCartStartMs - Date.now() <= CHECKIN_WINDOW_MS;
  useEffect(() => {
    if (checkinTouched) return;
    setCheckin(checkinSuggested);
  }, [checkinSuggested, checkinTouched]);

  // Aggregate amount syncs to cart subtotal until the operator overrides it.
  useEffect(() => {
    if (amountTouched || paymentMethod === 'comp') return;
    setAmountCents(subtotalCents);
  }, [subtotalCents, amountTouched, paymentMethod]);

  // Comp forces amount to 0; flipping back doesn't auto-restore.
  useEffect(() => {
    if (paymentMethod === 'comp') {
      setAmountCents(0);
      setAmountTouched(true);
    }
  }, [paymentMethod]);

  // ---- Closing with a live terminal payment --------------------------------
  // ✕ and Escape unmount the SuccessView and with it TerminalChargePanel. That
  // used to leave Stripe's reader action `in_progress`, so the WisePOS kept
  // showing the amount indefinitely (a walk-away is not a `failed` action, so
  // booking-terminal's eager clear never fires) and the NEXT counter sale was
  // refused with terminal_reader_busy. Free the reader first; never block the
  // close on it, so a dead reader cannot trap the operator in the modal.
  const posPanel = useRef<TerminalChargeHandle>(null);
  const closeAfterClearing = useCallback(
    async (created: boolean) => {
      await posPanel.current?.clearReader();
      onClose(created);
    },
    [onClose],
  );

  // ---- Close on Escape ----------------------------------------------------
  // Both exits go through closeAfterClearing: a POS payment may still be live
  // on the WisePOS, and closing the window has to free it (see below).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void closeAfterClearing(success != null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [closeAfterClearing, success]);

  // ---- Draft helpers ------------------------------------------------------
  const draftValid =
    !!pickedSlot && partySize >= 1 && partySize <= 30 && priceCents >= 0;

  const commitDraft = useCallback((): boolean => {
    if (!pickedSlot) return false;
    const experience = experiences.find((e) => e.id === experienceId);
    const location   = locations.find((l) => l.id === locationId);
    if (!experience || !location) return false;
    setCart((prev) => [
      ...prev,
      {
        experience,
        location,
        slot: pickedSlot,
        partySize,
        priceCents,
      },
    ]);
    // Reset draft for the next pick. Keep date + location + experience so
    // the operator can swap one field at a time (e.g. change experience but
    // stay on the same day) without re-picking everything.
    setPickedSlot(null);
    setPriceCents(0);
    setPriceTouched(false);
    setPartySize(2);
    return true;
  }, [pickedSlot, experiences, locations, experienceId, locationId, partySize, priceCents]);

  const removeCartPick = useCallback((idx: number) => {
    setCart((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  // ---- Validation per step ------------------------------------------------
  // Step 1 (slot) is "done" if EITHER the cart already has picks (operator
  // built earlier picks but emptied the draft) OR a valid draft exists.
  const slotStepValid = cart.length > 0 || draftValid;

  // First name always. With an e-mail (typed or required): last name + valid
  // e-mail as before. "Direct inchecken" + empty e-mail = anonymous booking:
  // first name is enough (last name optional). Quick mode's betaallink route
  // ALWAYS needs an e-mail (that's where the link goes), checkin or not.
  const emailTrimmed = email.trim();
  const emailLooksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrimmed);
  const linkChosen = mode === 'quick' && payVia === 'link';
  const emailOptional = checkin && !linkChosen;
  const anonymous = emailOptional && emailTrimmed.length === 0;
  const customerStepValid =
    firstName.trim().length > 0 &&
    (anonymous
      ? true
      : emailLooksValid && lastName.trim().length > 0);

  // Cumulative-capacity check across the cart. For each unique slot, sum
  // every pick's party_size and compare against (capacity − server-booked).
  // Picks at any slot whose cumulative load exceeds capacity are flagged so
  // the operator sees the force-checkbox in PaymentStep. The RPC enforces
  // the same rule server-side as a backstop.
  const overCapacityPicks = useMemo(() => {
    const overSlots = new Set<string>();
    const slotByKey = new Map<string, CartPick>();
    for (const p of allPicks) {
      const key = `${p.slot.room_id}|${p.slot.slot_start}`;
      if (!slotByKey.has(key)) slotByKey.set(key, p);
    }
    for (const [key, rep] of slotByKey) {
      const claimed = allClaims.get(key) ?? 0;
      if (rep.slot.booked + claimed > rep.slot.capacity) overSlots.add(key);
    }
    return allPicks.filter((p) =>
      overSlots.has(`${p.slot.room_id}|${p.slot.slot_start}`),
    );
  }, [allPicks, allClaims]);
  const isOverCapacity = overCapacityPicks.length > 0;

  // POS availability: the cart's location must have a configured reader.
  // (All picks share one location — the slot builder is location-scoped.)
  const cartLocationId = allPicks[0]?.slot.location_id ?? locationId;
  const posReaderId =
    locations.find((l) => l.id === cartLocationId)?.pos_reader_id ?? null;
  const posConfigured = posReaderId !== null;

  // Live reader probe while the payment step is open: an offline / deleted
  // reader is caught HERE, before a booking exists, instead of by a failing
  // `start` afterwards. Re-probed every 5 s (one tiny reader lookup — a
  // reader coming back online flips the option back by itself) and on the
  // manual "Opnieuw controleren" button. null = not probed yet / probe
  // failed (treated as usable: a Stripe hiccup must not block sales).
  const [readerState, setReaderState] = useState<TerminalReaderState | null>(null);
  const [readerProbeTick, setReaderProbeTick] = useState(0);
  useEffect(() => {
    // Quick mode shows the payment block on its single screen, so probe the
    // reader whenever the modal is in quick mode; the wizard probes on step 3.
    const paymentVisible = mode === 'quick' || step === 'payment';
    if (!paymentVisible || !posConfigured || !cartLocationId) {
      setReaderState(null);
      return;
    }
    let cancelled = false;
    const probe = async () => {
      const res = await terminalReaderStatus(cartLocationId);
      if (cancelled) return;
      setReaderState(res.ok && res.data ? res.data : null);
    };
    void probe();
    const interval = setInterval(() => void probe(), 5000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [mode, step, posConfigured, cartLocationId, readerProbeTick]);
  const posOffline = readerUnusable(readerState);
  const posBusy = readerState?.busy === true;
  const posAvailable = posConfigured && !posOffline;

  // Default the method to POS whenever a usable terminal is available and the
  // operator hasn't chosen otherwise; fall back to cash if it disappears or
  // turns out to be offline.
  useEffect(() => {
    if (paymentTouched) return;
    setPaymentMethod(posAvailable ? 'pos' : 'cash');
  }, [posAvailable, paymentTouched]);
  useEffect(() => {
    if (!posAvailable && paymentMethod === 'pos') setPaymentMethod('cash');
  }, [posAvailable, paymentMethod]);

  // Quick-mode route: POS while the reader is usable; an unusable reader
  // flips to betaallink by itself (and back, unless the operator chose).
  useEffect(() => {
    if (mode !== 'quick') return;
    if (!posAvailable && payVia === 'pos') setPayVia('link');
    else if (posAvailable && !payViaTouched && payVia === 'link') setPayVia('pos');
  }, [mode, posAvailable, payVia, payViaTouched]);

  // Quick-mode gate: POS needs a usable reader; betaallink needs the e-mail
  // (enforced inside customerStepValid); both need a positive amount (a
  // zero total is a comp — wizard territory).
  const quickSubmitValid =
    allPicks.length > 0 &&
    customerStepValid &&
    amountCents > 0 &&
    (payVia !== 'pos' || posAvailable) &&
    (!isOverCapacity || overCapacityAck);

  const paymentStepValid =
    allPicks.length > 0 &&
    customerStepValid &&
    amountCents >= 0 &&
    (paymentMethod !== 'comp' || amountCents === 0) &&
    (paymentMethod !== 'unpaid' || amountCents > 0) &&
    (paymentMethod !== 'pos' || (amountCents > 0 && posAvailable)) &&
    (paymentMethod !== 'giftcard' || (amountCents > 0 && canSellGiftCards)) &&
    (!isOverCapacity || overCapacityAck);

  // ---- Step transitions ---------------------------------------------------
  const advance = useCallback(() => {
    setSubmitError(null);
    if (step === 'slot') {
      // If the operator has a valid draft, commit it before advancing.
      if (draftValid) {
        if (!commitDraft()) return;
      } else if (cart.length === 0) {
        return;
      }
      setStep('customer');
    } else if (step === 'customer' && customerStepValid) {
      setStep('payment');
    }
  }, [step, draftValid, commitDraft, cart.length, customerStepValid]);

  const goBack = useCallback(() => {
    setSubmitError(null);
    if (step === 'customer') setStep('slot');
    else if (step === 'payment') setStep('customer');
  }, [step]);

  const addAnother = useCallback(() => {
    setSubmitError(null);
    if (!draftValid) return;
    if (!commitDraft()) return;
    // Stay on slot step. Draft already reset by commitDraft.
  }, [draftValid, commitDraft]);

  // ---- Submit -------------------------------------------------------------
  const handleSubmit = useCallback(async () => {
    const gate = mode === 'quick' ? quickSubmitValid : paymentStepValid;
    if (!gate || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Resolve start_time_id for every pick before building the payload.
      const resolved = await Promise.all(
        allPicks.map(async (p) => {
          const sid = await resolveStartTimeId(
            p.slot.room_id,
            p.slot.experience_id,
            p.slot.time_of_day,
            p.slot.slot_start,
          );
          if (!sid) {
            throw new Error(
              `Geen actief start_time gevonden voor (${p.slot.room_name}, ${p.slot.time_of_day}).`,
            );
          }
          return { pick: p, startTimeId: sid };
        }),
      );

      // Group picks into departures by shared slot_start (two picks belong to
      // the same departure iff their slot_starts match exactly — mirrors the
      // customer-widget grouping rule). Each pick has exactly one slot in v1.
      const byStart = new Map<string, typeof resolved>();
      for (const r of resolved) {
        const key = r.pick.slot.slot_start;
        const list = byStart.get(key) ?? [];
        list.push(r);
        byStart.set(key, list);
      }
      const starts = [...byStart.keys()].sort();
      const departuresWithIds: WalkinDepartureInput[] = starts.map((start) => ({
        experiences: byStart.get(start)!.map((r) => ({
          experience_id: r.pick.experience.id,
          price_cents:   r.pick.priceCents,
          slots: [
            {
              room_id:       r.pick.slot.room_id,
              start_time_id: r.startTimeId,
              slot_start:    r.pick.slot.slot_start,
              party_size:    r.pick.partySize,
              slot_index:    0,
            },
          ],
        })),
      }));

      // POS = create as 'unpaid' with no email yet; the terminal charge that
      // follows (and the Stripe webhook) settle it and send the ticket.
      // Quick mode knows only two routes: POS, or unpaid + betaallink.
      const isPos = mode === 'quick' ? payVia === 'pos' : paymentMethod === 'pos';
      const isGiftCard = mode !== 'quick' && paymentMethod === 'giftcard';
      const guestName = `${firstName.trim()} ${lastName.trim()}`.trim();
      const result = await createWalkinBooking({
        departures: departuresWithIds,
        // No e-mail (only possible with "Direct inchecken") = anonymous
        // booking: no customer row, the name lives on the booking.
        customer: anonymous
          ? null
          : {
              email: emailTrimmed.toLowerCase(),
              first_name: firstName.trim(),
              last_name: lastName.trim(),
              phone: phone.trim().length > 0 ? phone.trim() : null,
              locale,
              marketing_opt_in: false,
            },
        guest_name: guestName,
        checkin,
        // isPos no longer aliases a plain `paymentMethod === 'pos'` check, so
        // TS can't narrow 'pos' out of the else branch itself; every non-POS
        // wizard value IS a server method.
        payment_method: isPos || isGiftCard || mode === 'quick'
          ? 'unpaid'
          : (paymentMethod as WalkinPaymentMethod),
        force: isOverCapacity,
        locale,
        notes: notes.trim().length > 0 ? notes.trim() : null,
        discount_code: discountCode.trim().length > 0 ? discountCode.trim() : null,
        send_payment_link: isPos || isGiftCard
          ? false
          : mode === 'quick'
            ? true // quick non-POS = the betaallink route
            : paymentMethod === 'unpaid' ? sendPaymentLink : undefined,
        send_email: isPos || isGiftCard ? false : undefined,
      });

      if (!result.ok || !result.data) {
        const e = result.error;
        const msg = e?.message ?? e?.error ?? `HTTP ${result.status}`;
        setSubmitError(msg);
        return;
      }

      setSuccess({
        booking_id: result.data.booking_id,
        booking_number: result.data.booking_number,
        departures: result.data.departures,
        pos: isPos,
        giftCard: isGiftCard,
        checkin,
        anonymous,
      });
    } catch (e) {
      setSubmitError(formatError(e));
    } finally {
      setSubmitting(false);
    }
  }, [
    paymentStepValid, quickSubmitValid, mode, payVia, submitting, allPicks,
    emailTrimmed, anonymous, checkin, firstName, lastName, phone, locale,
    paymentMethod, sendPaymentLink, isOverCapacity, notes, discountCode,
  ]);

  // ---- Discount-code preview ----------------------------------------------
  // Cart-aware: every pick goes to fn_validate_discount_code_cart, the same
  // function booking-walkin-create runs, so multi-experience walk-ins can
  // carry a code (experience-scoped codes only count matching picks; a ticket
  // cap spans the cart; flat credit is split across the picks).
  const checkDiscount = useCallback(async () => {
    const codeStr = discountCode.trim();
    if (!codeStr || subtotalCents <= 0 || paymentMethod === 'comp') {
      setDiscountPreview(null);
      return;
    }
    setDiscountChecking(true);
    try {
      const r = await validateDiscountCodeCart(
        codeStr,
        allPicks.map((p) => ({
          experience_id:  p.experience.id,
          party_size:     p.partySize,
          subtotal_cents: p.priceCents,
        })),
      );
      setDiscountPreview({
        valid: r.valid,
        amountOffCents: r.amount_off_cents,
        reason: r.reason,
      });
    } catch {
      setDiscountPreview({ valid: false, reason: 'rpc_error' });
    } finally {
      setDiscountChecking(false);
    }
  }, [discountCode, subtotalCents, allPicks, paymentMethod]);

  const singleRoomLocations = useSingleRoomLocations();

  // ---- Render -------------------------------------------------------------
  return (
    <div
      className="dep-modal-backdrop"
      onClick={() => {
        if (success != null) onClose(true);
      }}
    >
      <div
        className="dep-modal new-booking-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="dep-modal-head">
          <div>
            <h2>Nieuwe boeking</h2>
            <div className="dep-modal-sub muted">
              {success
                ? 'Aangemaakt'
                : mode === 'quick'
                  ? 'Snelle boeking'
                  : step === 'slot'
                    ? 'Stap 1 van 3 — expedities'
                    : step === 'customer'
                      ? 'Stap 2 van 3 — klantgegevens'
                      : 'Stap 3 van 3 — betaling'}
            </div>
          </div>
          <button
            className="btn-link dep-modal-close"
            onClick={() => void closeAfterClearing(success != null)}
            aria-label="Sluiten"
          >
            ✕
          </button>
        </header>

        <div className="dep-modal-body">
          {success ? (
            <SuccessView
              bookingId={success.booking_id}
              bookingNumber={success.booking_number}
              departures={success.departures}
              pos={success.pos}
              giftCard={success.giftCard}
              posAvailable={posAvailable}
              checkin={success.checkin}
              anonymous={success.anonymous}
              strictPos={mode === 'quick'}
              panelRef={posPanel}
              onClose={() => onClose(true)}
            />
          ) : mode === 'quick' ? (
            // ---- QUICK: everything on one screen -------------------------
            <div className="walkin-step">
              <div className="walkin-quick-chips">
                <button
                  type="button"
                  className={`walkin-quick-chip ${editing === 'date' ? 'walkin-quick-chip-active' : ''}`}
                  onClick={() => setEditing(editing === 'date' ? null : 'date')}
                  title="Datum aanpassen"
                >
                  {fmtChipDate(date)}
                </button>
                <button
                  type="button"
                  className={`walkin-quick-chip ${editing === 'time' ? 'walkin-quick-chip-active' : ''}`}
                  onClick={() => setEditing(editing === 'time' ? null : 'time')}
                  title="Tijdslot aanpassen"
                >
                  {pickedSlot?.time_of_day ?? prefillTime ?? 'kies tijd'}
                </button>
                <button
                  type="button"
                  className={`walkin-quick-chip ${editing === 'pick' ? 'walkin-quick-chip-active' : ''}`}
                  onClick={() => setEditing(editing === 'pick' ? null : 'pick')}
                  title="Experience aanpassen"
                >
                  {experiences.find((e) => e.id === experienceId)?.name ?? 'Kies experience'}
                </button>
                {locations.length > 1 ? (
                  <button
                    type="button"
                    className={`walkin-quick-chip ${editing === 'location' ? 'walkin-quick-chip-active' : ''}`}
                    onClick={() => setEditing(editing === 'location' ? null : 'location')}
                    title="Locatie aanpassen"
                  >
                    {(() => {
                      const l = locations.find((x) => x.id === locationId);
                      return l?.display_name ?? l?.name ?? 'Locatie';
                    })()}
                  </button>
                ) : null}
                <button
                  type="button"
                  className={`walkin-quick-chip ${editing === 'pick' ? 'walkin-quick-chip-active' : ''}`}
                  onClick={() => setEditing(editing === 'pick' ? null : 'pick')}
                  title="Aantal personen aanpassen"
                >
                  {partySize}p
                </button>
                <button
                  type="button"
                  className={`walkin-quick-chip ${editing === 'price' ? 'walkin-quick-chip-active' : ''}`}
                  onClick={() => setEditing(editing === 'price' ? null : 'price')}
                  title="Prijs aanpassen (volgt uit aantal personen)"
                >
                  € {(priceCents / 100).toFixed(2)}
                </button>
                <button
                  type="button"
                  className="walkin-quick-chip"
                  onClick={() => setLocale(locale === 'nl' ? 'en' : 'nl')}
                  title="Taal van de klant (tik om te wisselen)"
                >
                  {locale === 'en' ? '🇬🇧 EN' : '🇳🇱 NL'}
                </button>
              </div>

              {refError ? <div className="error-card">{refError}</div> : null}

              {editing === 'date' ? (
                <label className="walkin-field">
                  <span>Datum</span>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => {
                      if (e.target.value) setDate(e.target.value);
                    }}
                  />
                </label>
              ) : null}

              {editing === 'location' && locations.length > 1 ? (
                <label className="walkin-field">
                  <span>Locatie</span>
                  <select
                    value={locationId}
                    onChange={(e) => {
                      setLocationId(e.target.value);
                      // New location = new popularity default + new slot list.
                      setExperienceId('');
                      setPickedSlot(null);
                    }}
                  >
                    {locations.map((l) => (
                      <option key={l.id} value={l.id}>
                        {l.display_name ?? l.name}
                      </option>
                    ))}
                  </select>
                </label>
              ) : null}

              {editing === 'pick' ? (
                <div className="walkin-row">
                  <label className="walkin-field">
                    <span>Experience</span>
                    <select
                      value={experienceId}
                      onChange={(e) => setExperienceId(e.target.value)}
                    >
                      <option value="">— kies —</option>
                      {experiences.map((e) => (
                        <option key={e.id} value={e.id}>
                          {e.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className="walkin-field walkin-party">
                    <span>Aantal personen</span>
                    <PartySizeInput value={partySize} onChange={setPartySize} />
                  </label>
                </div>
              ) : null}

              {editing === 'price' ? (
                <label className="walkin-field">
                  <span>Prijs (€) — voorstel volgt uit aantal personen</span>
                  <MoneyInput
                    cents={priceCents}
                    disabled={!pickedSlot}
                    onCents={(c) => {
                      setPriceTouched(true);
                      setPriceCents(c);
                    }}
                  />
                </label>
              ) : null}

              {editing === 'time' || (!pickedSlot && editing === null) ? (
                <div className="walkin-slots">
                  <div className="walkin-section-head">Tijdslot</div>
                  {refLoading || slotsLoading ? (
                    <div className="muted">Slots ophalen…</div>
                  ) : slotsError ? (
                    <div className="error-card">{slotsError}</div>
                  ) : !experienceId ? (
                    <div className="muted">Kies eerst een experience.</div>
                  ) : slots.length === 0 ? (
                    <div className="muted">Geen slots op deze datum.</div>
                  ) : (
                    <SlotList
                      slots={slots}
                      cartClaims={cartClaims}
                      pickedSlot={pickedSlot}
                      onSlot={(s) => {
                        setPickedSlot(s);
                        setEditing(null);
                      }}
                      singleRoomLocations={singleRoomLocations}
                    />
                  )}
                </div>
              ) : null}

              <CartChips cart={cart} onRemove={removeCartPick} />

              <CustomerStep
                quick
                emailRequired={linkChosen}
                firstName={firstName}
                lastName={lastName}
                email={email}
                phone={phone}
                locale={locale}
                checkin={checkin}
                checkinSuggested={checkinSuggested}
                emailInvalid={emailTrimmed.length > 0 && !emailLooksValid}
                onFirstName={setFirstName}
                onLastName={setLastName}
                onEmail={setEmail}
                onPhone={setPhone}
                onLocale={setLocale}
                onCheckin={(v) => {
                  setCheckinTouched(true);
                  setCheckin(v);
                }}
              />

              {/* ---- Betaling: POS eerst, betaallink als vangnet ---------- */}
              <div className="walkin-quick-pay">
                <div className="walkin-section-head">Betaling</div>
                <div className="walkin-quick-chips">
                  <button
                    type="button"
                    className={`walkin-quick-chip ${payVia === 'pos' ? 'walkin-quick-chip-active' : ''}`}
                    disabled={!posAvailable}
                    title={
                      !posConfigured
                        ? 'Geen pinterminal ingesteld voor deze locatie'
                        : posOffline
                          ? readerProblemText(readerState) ?? 'Pinterminal niet beschikbaar'
                          : undefined
                    }
                    onClick={() => {
                      setPayViaTouched(true);
                      setPayVia('pos');
                    }}
                  >
                    Pinterminal{posBusy ? ' (bezet)' : ''}
                  </button>
                  <button
                    type="button"
                    className={`walkin-quick-chip ${payVia === 'link' ? 'walkin-quick-chip-active' : ''}`}
                    onClick={() => {
                      setPayViaTouched(true);
                      setPayVia('link');
                    }}
                  >
                    Betaallink via e-mail
                  </button>
                </div>
                {payVia === 'pos' ? (
                  <p className="muted walkin-hint">
                    Na bevestigen gaat € {(amountCents / 100).toFixed(2)} direct naar de pinterminal.
                    {checkin ? ' Inchecken volgt zodra de betaling binnen is.' : ''}
                    {' '}Mislukt de betaling, dan kun je opnieuw proberen of de boeking annuleren.
                  </p>
                ) : (
                  <p className="muted walkin-hint">
                    De boeking wordt aangemaakt als "Nog niet betaald" en de klant krijgt een
                    e-mail met betaallink{checkin ? '; inchecken gebeurt direct' : ''}.
                    E-mailadres is hiervoor verplicht.
                  </p>
                )}
                {posConfigured && posOffline ? (
                  <div className="walkin-warning">
                    <strong>{readerProblemText(readerState) ?? 'Pinterminal niet beschikbaar.'}</strong>{' '}
                    Zodra de terminal online is wordt "Pinterminal" hier weer beschikbaar.
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => setReaderProbeTick((t) => t + 1)}
                      style={{ marginLeft: 8 }}
                    >
                      Opnieuw controleren
                    </button>
                  </div>
                ) : null}

                {isOverCapacity ? (
                  <div className="walkin-warning">
                    <strong>Eén of meer slots zit vol.</strong>
                    <label className="walkin-warn-ack">
                      <input
                        type="checkbox"
                        checked={overCapacityAck}
                        onChange={(e) => setOverCapacityAck(e.target.checked)}
                      />
                      <span>Ja, boek toch over capaciteit heen</span>
                    </label>
                  </div>
                ) : null}

                <div className="walkin-quick-extras">
                  {!showVoucher ? (
                    <button type="button" className="btn-link" onClick={() => setShowVoucher(true)}>
                      Kortingscode…
                    </button>
                  ) : (
                    <label className="walkin-field">
                      <span>
                        Voucher / kortingscode
                        {allPicks.length > 1 ? (
                          <span className="muted"> — niet beschikbaar voor meerdere expedities</span>
                        ) : null}
                      </span>
                      <div className="walkin-discount-row">
                        <input
                          type="text"
                          value={discountCode}
                          disabled={allPicks.length > 1}
                          onChange={(e) => {
                            setDiscountCode(e.target.value.toUpperCase());
                            setDiscountPreview(null);
                          }}
                          onBlur={() => void checkDiscount()}
                          placeholder="WELCOME10"
                          autoFocus
                        />
                        <button
                          type="button"
                          className="btn-link"
                          onClick={() => void checkDiscount()}
                          disabled={discountChecking || discountCode.trim().length === 0}
                        >
                          {discountChecking ? '…' : 'Controleer'}
                        </button>
                      </div>
                      {/* Totaal is alleen interessant mét korting; dit is de ene plek. */}
                      {discountPreview ? (
                        discountPreview.valid && discountPreview.amountOffCents != null ? (
                          <span className="walkin-discount-ok">
                            – €{(discountPreview.amountOffCents / 100).toFixed(2)} korting
                            {' → totaal € '}
                            {((amountCents - discountPreview.amountOffCents) / 100).toFixed(2)}
                          </span>
                        ) : (
                          <span className="walkin-discount-bad">
                            Code niet geldig: {discountPreview.reason ?? 'onbekend'}
                          </span>
                        )
                      ) : null}
                    </label>
                  )}
                  {!showNote ? (
                    <button type="button" className="btn-link" onClick={() => setShowNote(true)}>
                      Notitie…
                    </button>
                  ) : (
                    <label className="walkin-field">
                      <span>Notitie</span>
                      <textarea
                        value={notes}
                        rows={2}
                        onChange={(e) => setNotes(e.target.value)}
                        autoFocus
                        placeholder="Bijv. tel. boeking, gast verjaardag, …"
                      />
                    </label>
                  )}
                </div>
              </div>

              {submitError && (
                <div className="error-card walkin-submit-error">{submitError}</div>
              )}

              <div className="walkin-step-actions">
                <button
                  className="btn-link"
                  disabled={submitting}
                  title="Nog een expeditie aan deze boeking toevoegen (opent de uitgebreide flow)"
                  onClick={() => {
                    if (draftValid) commitDraft();
                    setMode('wizard');
                    setStep('slot');
                  }}
                >
                  + Meer expedities
                </button>
                <button
                  className="walkin-primary-btn"
                  disabled={!paymentStepValid || submitting}
                  onClick={() => void handleSubmit()}
                >
                  {submitting ? 'Bezig…' : 'Boeking bevestigen'}
                </button>
              </div>
            </div>
          ) : (
            <>
              {step === 'slot' && (
                <SlotStep
                  refLoading={refLoading}
                  refError={refError}
                  locations={locations}
                  experiences={experiences}
                  cart={cart}
                  cartClaims={cartClaims}
                  onRemoveCart={removeCartPick}
                  locationId={locationId}
                  experienceId={experienceId}
                  date={date}
                  slots={slots}
                  slotsLoading={slotsLoading}
                  slotsError={slotsError}
                  pickedSlot={pickedSlot}
                  partySize={partySize}
                  priceCents={priceCents}
                  onLocation={setLocationId}
                  onExperience={setExperienceId}
                  onDate={setDate}
                  onSlot={setPickedSlot}
                  onPartySize={setPartySize}
                  onPrice={(c) => {
                    setPriceTouched(true);
                    setPriceCents(c);
                  }}
                />
              )}

              {step === 'customer' && (
                <CustomerStep
                  firstName={firstName}
                  lastName={lastName}
                  email={email}
                  phone={phone}
                  locale={locale}
                  checkin={checkin}
                  checkinSuggested={checkinSuggested}
                  emailInvalid={emailTrimmed.length > 0 && !emailLooksValid}
                  onFirstName={setFirstName}
                  onLastName={setLastName}
                  onEmail={setEmail}
                  onPhone={setPhone}
                  onLocale={setLocale}
                  onCheckin={(v) => {
                    setCheckinTouched(true);
                    setCheckin(v);
                  }}
                />
              )}

              {step === 'payment' && allPicks.length > 0 && (
                <PaymentStep
                  cart={allPicks}
                  customerName={`${firstName.trim()} ${lastName.trim()}`.trim()}
                  email={emailTrimmed}
                  phone={phone.trim()}
                  locale={locale}
                  checkin={checkin}
                  anonymous={anonymous}
                  paymentMethod={paymentMethod}
                  amountCents={amountCents}
                  subtotalCents={subtotalCents}
                  totalPartySize={totalPartySize}
                  notes={notes}
                  overCapacityPicks={overCapacityPicks}
                  isOverCapacity={isOverCapacity}
                  overCapacityAck={overCapacityAck}
                  discountCode={discountCode}
                  discountPreview={discountPreview}
                  discountChecking={discountChecking}
                  sendPaymentLink={sendPaymentLink}
                  onSendPaymentLink={setSendPaymentLink}
                  posAvailable={posAvailable}
                  giftCardAllowed={canSellGiftCards}
                  posConfigured={posConfigured}
                  posOffline={posOffline}
                  posBusy={posBusy}
                  posReaderProblem={readerProblemText(readerState)}
                  onReaderRecheck={() => setReaderProbeTick((t) => t + 1)}
                  onPaymentMethod={(m) => {
                    setPaymentTouched(true);
                    setPaymentMethod(m);
                  }}
                  onAmountCents={(c) => {
                    setAmountTouched(true);
                    setAmountCents(c);
                  }}
                  onNotes={setNotes}
                  onOverCapacityAck={setOverCapacityAck}
                  onDiscountCode={(v) => {
                    setDiscountCode(v);
                    setDiscountPreview(null);
                  }}
                  onDiscountCheck={() => void checkDiscount()}
                />
              )}

              {submitError && (
                <div className="error-card walkin-submit-error">
                  {submitError}
                </div>
              )}

              <div className="walkin-step-actions">
                {step !== 'slot' ? (
                  <button
                    className="btn-link"
                    onClick={goBack}
                    disabled={submitting}
                  >
                    ‹ Terug
                  </button>
                ) : (
                  <span />
                )}

                {step === 'slot' ? (
                  <div className="walkin-slot-cta-group">
                    <button
                      className="btn-link"
                      type="button"
                      onClick={addAnother}
                      disabled={!draftValid || submitting}
                      title={
                        !draftValid
                          ? 'Kies eerst een slot om toe te voegen'
                          : 'Voeg deze expeditie toe en blijf hier om er nog één toe te voegen'
                      }
                    >
                      + Voeg toe en nog één
                    </button>
                    <button
                      className="walkin-primary-btn"
                      type="button"
                      disabled={!slotStepValid || submitting}
                      onClick={advance}
                    >
                      Volgende ›
                    </button>
                  </div>
                ) : step === 'payment' ? (
                  <button
                    className="walkin-primary-btn"
                    disabled={!paymentStepValid || submitting}
                    onClick={() => void handleSubmit()}
                  >
                    {submitting ? 'Bezig…' : 'Boeking bevestigen'}
                  </button>
                ) : (
                  <button
                    className="walkin-primary-btn"
                    disabled={!customerStepValid || submitting}
                    onClick={advance}
                  >
                    Volgende ›
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Cart chips — picks already added, rendered above the slot form. Remove via ×.
// -----------------------------------------------------------------------------
function CartChips({
  cart,
  onRemove,
}: {
  cart: CartPick[];
  onRemove: (idx: number) => void;
}) {
  if (cart.length === 0) return null;
  return (
    <div className="walkin-cart-chips">
      {cart.map((p, i) => (
        <span key={`${p.slot.room_id}-${p.slot.slot_start}-${p.experience.id}-${i}`} className="walkin-cart-chip">
          <strong>{p.experience.name}</strong>
          <span className="muted">
            {' '}· {p.slot.time_of_day} · {p.partySize}p · €{(p.priceCents / 100).toFixed(2)}
          </span>
          <button
            type="button"
            className="walkin-cart-chip-x"
            onClick={() => onRemove(i)}
            aria-label={`Verwijder ${p.experience.name}`}
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Shared slot list — used by the wizard's SlotStep and quick mode's time
// editor. Auto-centers the picked slot whenever the list refreshes.
// -----------------------------------------------------------------------------
function SlotList({
  slots, cartClaims, pickedSlot, onSlot, singleRoomLocations,
}: {
  slots: Departure[];
  cartClaims: Map<string, number>;
  pickedSlot: Departure | null;
  onSlot: (v: Departure) => void;
  singleRoomLocations: Set<string>;
}) {
  const listRef = useRef<HTMLUListElement | null>(null);
  const lastCenteredKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!pickedSlot || slots.length === 0) return;
    const key = `${pickedSlot.room_id}|${pickedSlot.slot_start}|${pickedSlot.experience_id}`;
    if (lastCenteredKeyRef.current === key) return;
    const list = listRef.current;
    if (!list) return;
    const el = list.querySelector<HTMLElement>('.walkin-slot-picked');
    if (!el) return;
    const listRect = list.getBoundingClientRect();
    const elRect   = el.getBoundingClientRect();
    const offsetWithinList = (elRect.top - listRect.top) + list.scrollTop;
    const target = offsetWithinList - (list.clientHeight - el.clientHeight) / 2;
    const max = list.scrollHeight - list.clientHeight;
    list.scrollTop = Math.max(0, Math.min(target, max));
    lastCenteredKeyRef.current = key;
  }, [pickedSlot, slots.length]);

  return (
    <ul className="walkin-slot-list" ref={listRef}>
      {slots.map((s) => {
        const claimed = cartClaims.get(`${s.room_id}|${s.slot_start}`) ?? 0;
        const bookedEff = s.booked + claimed;
        const remaining = Math.max(0, s.capacity - bookedEff);
        const full = remaining <= 0;
        const isPicked =
          pickedSlot != null &&
          pickedSlot.room_id === s.room_id &&
          pickedSlot.slot_start === s.slot_start;
        const showRoom = !singleRoomLocations.has(s.location_id);
        return (
          <li key={`${s.room_id}-${s.slot_start}`}>
            <button
              type="button"
              className={`walkin-slot ${
                isPicked ? 'walkin-slot-picked' : ''
              } ${full ? 'walkin-slot-full' : ''}`}
              onClick={() => onSlot(s)}
            >
              <span className="walkin-slot-time">{s.time_of_day}</span>
              {showRoom ? (
                <span className="walkin-slot-room muted">
                  {s.room_name}
                </span>
              ) : (
                <span />
              )}
              <span
                className={`walkin-slot-cap ${
                  full ? 'walkin-slot-cap-full' : ''
                }`}
              >
                {bookedEff} / {s.capacity}
                {full ? ' · vol' : ''}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

// -----------------------------------------------------------------------------
// Step 1 — slot picker (with cart chips at the top)
// -----------------------------------------------------------------------------
function SlotStep({
  refLoading, refError, locations, experiences,
  cart, cartClaims, onRemoveCart,
  locationId, experienceId, date,
  slots, slotsLoading, slotsError,
  pickedSlot, partySize, priceCents,
  onLocation, onExperience, onDate, onSlot, onPartySize, onPrice,
}: {
  refLoading: boolean;
  refError: string | null;
  locations: BookingLocation[];
  experiences: BookingExperience[];
  cart: CartPick[];
  cartClaims: Map<string, number>;
  onRemoveCart: (idx: number) => void;
  locationId: string;
  experienceId: string;
  date: string;
  slots: Departure[];
  slotsLoading: boolean;
  slotsError: string | null;
  pickedSlot: Departure | null;
  partySize: number;
  priceCents: number;
  onLocation: (v: string) => void;
  onExperience: (v: string) => void;
  onDate: (v: string) => void;
  onSlot: (v: Departure) => void;
  onPartySize: (v: number) => void;
  onPrice: (v: number) => void;
}) {
  const singleRoomLocations = useSingleRoomLocations();

  if (refLoading) return <div className="muted">Laden…</div>;
  if (refError) return <div className="error-card">{refError}</div>;

  return (
    <div className="walkin-step">
      <CartChips cart={cart} onRemove={onRemoveCart} />

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Locatie</span>
          <select
            value={locationId}
            onChange={(e) => onLocation(e.target.value)}
          >
            <option value="">— kies —</option>
            {locations.map((l) => (
              <option key={l.id} value={l.id}>
                {l.display_name ?? l.name}
              </option>
            ))}
          </select>
        </label>

        <label className="walkin-field">
          <span>Experience</span>
          <select
            value={experienceId}
            onChange={(e) => onExperience(e.target.value)}
          >
            <option value="">— kies —</option>
            {experiences.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
        </label>

        <label className="walkin-field">
          <span>Datum</span>
          <input
            type="date"
            value={date}
            onChange={(e) => e.target.value && onDate(e.target.value)}
          />
        </label>
      </div>

      <div className="walkin-slots">
        <div className="walkin-section-head">Tijdslots</div>
        {!locationId || !experienceId ? (
          <div className="muted">Kies locatie en experience.</div>
        ) : slotsLoading ? (
          <div className="muted">Slots ophalen…</div>
        ) : slotsError ? (
          <div className="error-card">{slotsError}</div>
        ) : slots.length === 0 ? (
          <div className="muted">Geen slots op deze datum.</div>
        ) : (
          <SlotList
            slots={slots}
            cartClaims={cartClaims}
            pickedSlot={pickedSlot}
            onSlot={onSlot}
            singleRoomLocations={singleRoomLocations}
          />
        )}
      </div>

      <div className="walkin-row">
        <label className="walkin-field walkin-party">
          <span>Aantal personen</span>
          <PartySizeInput value={partySize} onChange={onPartySize} />
        </label>

        <label className="walkin-field">
          <span>Prijs (€)</span>
          <MoneyInput
            cents={priceCents}
            disabled={!pickedSlot}
            onCents={onPrice}
          />
        </label>
      </div>

      {cart.length > 0 && (
        <p className="muted walkin-hint">
          Klik <strong>“+ Voeg toe en nog één”</strong> om een tweede expeditie aan deze boeking toe te voegen.
        </p>
      )}
    </div>
  );
}

function PartySizeInput({
  value,
  onChange,
}: {
  value: number;
  onChange: (v: number) => void;
}) {
  const [text, setText] = useState<string>(String(value));
  const lastExternalRef = useRef(value);
  useEffect(() => {
    if (value !== lastExternalRef.current) {
      lastExternalRef.current = value;
      setText(String(value));
    }
  }, [value]);
  // Stepper for touch use: the number input's native spinners are unusably
  // small on a phone, so − / + get real 44px-class buttons.
  const step = (delta: number) => {
    const next = Math.max(1, Math.min(30, value + delta));
    lastExternalRef.current = next;
    setText(String(next));
    onChange(next);
  };
  return (
    <div className="walkin-stepper">
      <button
        type="button"
        className="walkin-stepper-btn"
        onClick={() => step(-1)}
        disabled={value <= 1}
        aria-label="Eén persoon minder"
      >
        −
      </button>
      <input
        type="number"
        min={1}
        max={30}
        inputMode="numeric"
        value={text}
        onChange={(e) => {
          const v = e.target.value;
          setText(v);
          const n = Number(v);
          if (v !== '' && Number.isInteger(n) && n >= 1 && n <= 30) {
            lastExternalRef.current = n;
            onChange(n);
          }
        }}
        onBlur={() => {
          const n = Number(text);
          const clamped = !Number.isFinite(n) || n < 1
            ? 1
            : n > 30
              ? 30
              : Math.floor(n);
          lastExternalRef.current = clamped;
          onChange(clamped);
          setText(String(clamped));
        }}
      />
      <button
        type="button"
        className="walkin-stepper-btn"
        onClick={() => step(1)}
        disabled={value >= 30}
        aria-label="Eén persoon meer"
      >
        +
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Step 2 — customer
// -----------------------------------------------------------------------------
function CustomerStep({
  firstName, lastName, email, phone, locale,
  checkin, checkinSuggested, emailInvalid,
  quick = false, emailRequired = false,
  onFirstName, onLastName, onEmail, onPhone, onLocale, onCheckin,
}: {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  locale: string;
  checkin: boolean;
  checkinSuggested: boolean;
  emailInvalid: boolean;
  // Quick mode: only Voornaam up front; achternaam / e-mail / telefoon sit
  // behind a reveal link (taal is a chip in the quick header, so the select
  // is hidden too).
  quick?: boolean;
  // Betaallink route: the e-mail is where the link goes — force the fields
  // open and mark e-mail required regardless of "Direct inchecken".
  emailRequired?: boolean;
  onFirstName: (v: string) => void;
  onLastName: (v: string) => void;
  onEmail: (v: string) => void;
  onPhone: (v: string) => void;
  onLocale: (v: string) => void;
  onCheckin: (v: boolean) => void;
}) {
  const [showMore, setShowMore] = useState(false);
  const hasOptionalContent =
    email.trim().length > 0 || lastName.trim().length > 0 || phone.trim().length > 0;
  const expanded = !quick || showMore || emailRequired || hasOptionalContent;
  const noEmail = checkin && !emailRequired && email.trim().length === 0;
  return (
    <div className="walkin-step">
      <label className="walkin-warn-ack walkin-checkin-toggle">
        <input
          type="checkbox"
          checked={checkin}
          onChange={(e) => onCheckin(e.target.checked)}
        />
        <span>
          <strong>Direct inchecken</strong>
          <span className="muted">
            {checkinSuggested
              ? ' — het vertrek begint binnen 40 minuten'
              : ' — klant staat nu aan de balie'}
          </span>
        </span>
      </label>
      {!quick ? (
        <p className="muted walkin-hint">
          {checkin
            ? 'De klant wordt direct ingecheckt (bij pinterminal: zodra de betaling binnen is). E-mail is dan optioneel; zonder e-mail wordt er geen ticketmail gestuurd en wordt alleen de naam bij de boeking bewaard.'
            : 'Zonder direct inchecken is een e-mailadres verplicht (ticketmail met QR-code).'}
        </p>
      ) : null}

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Voornaam</span>
          <input
            type="text"
            value={firstName}
            onChange={(e) => onFirstName(e.target.value)}
            autoFocus={!quick}
          />
        </label>
        {expanded ? (
          <label className="walkin-field">
            <span>Achternaam{noEmail ? ' (optioneel)' : ''}</span>
            <input
              type="text"
              value={lastName}
              onChange={(e) => onLastName(e.target.value)}
            />
          </label>
        ) : null}
      </div>

      {!expanded ? (
        <button
          type="button"
          className="btn-link walkin-more-fields"
          onClick={() => setShowMore(true)}
        >
          + E-mail / meer velden
        </button>
      ) : (
        <>
          <label className="walkin-field">
            <span>
              Email
              {emailRequired
                ? ' (verplicht voor de betaallink)'
                : checkin ? ' (optioneel)' : ''}
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => onEmail(e.target.value)}
              autoComplete="off"
              placeholder={checkin && !emailRequired ? 'leeg = geen ticketmail' : undefined}
            />
            {emailInvalid ? (
              <span className="walkin-discount-bad">Dit is geen geldig e-mailadres.</span>
            ) : null}
          </label>

          <div className="walkin-row">
            <label className="walkin-field">
              <span>Telefoon (optioneel)</span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => onPhone(e.target.value)}
              />
            </label>

            {!quick ? (
              <label className="walkin-field">
                <span>Taal</span>
                <select value={locale} onChange={(e) => onLocale(e.target.value)}>
                  {LOCALES.map((l) => (
                    <option key={l.code} value={l.code}>
                      {l.label}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
          </div>
        </>
      )}

      {!quick ? (
        <p className="muted walkin-hint">
          {noEmail
            ? 'Geen e-mail: geen ticketmail, geen herinneringen. De boeking is op naam terug te vinden.'
            : 'Eén bevestigingsmail per vertrek (met QR-code) gaat na bevestigen automatisch uit.'}
        </p>
      ) : null}
    </div>
  );
}

// -----------------------------------------------------------------------------
// Step 3 — payment + over-capacity ack
// -----------------------------------------------------------------------------
function PaymentStep({
  cart, customerName, email, phone, locale, checkin, anonymous, compact = false,
  paymentMethod, amountCents, subtotalCents, totalPartySize, notes,
  overCapacityPicks, isOverCapacity, overCapacityAck,
  discountCode, discountPreview, discountChecking,
  sendPaymentLink, onSendPaymentLink, posAvailable, giftCardAllowed = false,
  posConfigured, posOffline, posBusy, posReaderProblem, onReaderRecheck,
  onPaymentMethod, onAmountCents, onNotes, onOverCapacityAck,
  onDiscountCode, onDiscountCheck,
}: {
  cart: CartPick[];
  customerName: string;
  email: string;
  phone: string;
  locale: string;
  checkin: boolean;
  anonymous: boolean;
  // Quick mode: the chips above already summarize the booking — skip the
  // summary block and render only the payment inputs.
  compact?: boolean;
  paymentMethod: UiPaymentMethod;
  posAvailable: boolean;    // configured AND not offline/missing
  giftCardAllowed?: boolean; // operator holds booking_gift_card_sell
  posConfigured: boolean;   // location has a reader at all
  posOffline: boolean;      // offline or deleted in Stripe
  posBusy: boolean;
  posReaderProblem: string | null;
  onReaderRecheck: () => void;
  amountCents: number;
  subtotalCents: number;
  totalPartySize: number;
  notes: string;
  overCapacityPicks: CartPick[];
  isOverCapacity: boolean;
  overCapacityAck: boolean;
  discountCode: string;
  discountPreview: { valid: boolean; amountOffCents?: number; reason?: string } | null;
  discountChecking: boolean;
  sendPaymentLink: boolean;
  onSendPaymentLink: (v: boolean) => void;
  onPaymentMethod: (v: UiPaymentMethod) => void;
  onAmountCents: (v: number) => void;
  onNotes: (v: string) => void;
  onOverCapacityAck: (v: boolean) => void;
  onDiscountCode: (v: string) => void;
  onDiscountCheck: () => void;
}) {
  const subtotalEur = useMemo(() => (subtotalCents / 100).toFixed(2), [subtotalCents]);
  const singleRoomLocations = useSingleRoomLocations();

  return (
    <div className="walkin-step">
      {!compact ? (
      <div className="walkin-summary">
        <div>
          <strong>Boeking</strong>
          <span className="muted"> · {cart.length} expeditie{cart.length === 1 ? '' : 's'} · {totalPartySize}p</span>
        </div>
        <ul className="walkin-summary-picks">
          {cart.map((p, i) => {
            const showRoom = !singleRoomLocations.has(p.slot.location_id);
            return (
              <li key={`${p.slot.room_id}-${p.slot.slot_start}-${p.experience.id}-${i}`}>
                <span>{p.experience.name}</span>
                <span className="muted">
                  {' · '}{p.slot.time_of_day}
                  {showRoom ? <> · {p.slot.room_name}</> : null}
                  {' · '}{p.partySize}p
                </span>
                <span className="walkin-summary-pick-price">
                  €{(p.priceCents / 100).toFixed(2)}
                </span>
              </li>
            );
          })}
        </ul>
        <div className="walkin-summary-subtotal muted">
          Subtotaal: €{subtotalEur}
        </div>
        <div className="muted">
          {customerName || '—'}
          {email ? <> · {email}</> : anonymous ? <> · geen e-mail (balie)</> : null}
          {phone ? <> · {phone}</> : null}
          {' · '}
          {locale.toUpperCase()}
          {checkin ? <> · <strong>direct inchecken</strong></> : null}
        </div>
      </div>
      ) : null}

      {isOverCapacity && (
        <div className="walkin-warning">
          <strong>Eén of meer slots zit vol.</strong>{' '}
          {overCapacityPicks.length === 1 ? (
            <>
              {overCapacityPicks[0]!.experience.name} @ {overCapacityPicks[0]!.slot.time_of_day}:
              {' '}{overCapacityPicks[0]!.slot.booked} / {overCapacityPicks[0]!.slot.capacity} geboekt,
              {' '}jij wilt {overCapacityPicks[0]!.partySize} personen plaatsen.
            </>
          ) : (
            <>
              {overCapacityPicks.length} expedities boven capaciteit.
            </>
          )}
          <label className="walkin-warn-ack">
            <input
              type="checkbox"
              checked={overCapacityAck}
              onChange={(e) => onOverCapacityAck(e.target.checked)}
            />
            <span>Ja, boek toch over capaciteit heen</span>
          </label>
        </div>
      )}

      <div className="walkin-row">
        <label className="walkin-field">
          <span>Betaalmethode</span>
          <select
            value={paymentMethod}
            onChange={(e) => onPaymentMethod(e.target.value as UiPaymentMethod)}
          >
            {(Object.keys(PAYMENT_LABEL) as UiPaymentMethod[])
              .filter((k) => k !== 'giftcard' || giftCardAllowed)
              .map((k) => (
              // POS is always listed; greyed out (with the reason) when the
              // location has no reader configured or the reader is unusable.
              <option key={k} value={k} disabled={k === 'pos' && (!posConfigured || posOffline)}>
                {PAYMENT_LABEL[k]}
                {k === 'pos' && !posConfigured ? ' — geen pinterminal ingesteld' : ''}
                {k === 'pos' && posConfigured && posOffline ? ' — niet beschikbaar' : ''}
                {k === 'pos' && posConfigured && !posOffline && posBusy ? ' — bezet' : ''}
              </option>
            ))}
          </select>
        </label>

        <label className="walkin-field">
          <span>Totaal (€)</span>
          <MoneyInput
            cents={amountCents}
            disabled={paymentMethod === 'comp'}
            onCents={onAmountCents}
          />
        </label>
      </div>

      {posConfigured && posOffline ? (
        <div className="walkin-warning">
          <strong>{posReaderProblem ?? 'Pinterminal niet beschikbaar.'}</strong>{' '}
          Er wordt elke paar seconden opnieuw gecontroleerd; zodra de terminal
          online is, wordt "Pinterminal (POS)" weer beschikbaar. Of kies een andere
          betaalmethode.
          <button type="button" className="btn-link" onClick={onReaderRecheck} style={{ marginLeft: 8 }}>
            Opnieuw controleren
          </button>
        </div>
      ) : null}

      {paymentMethod === 'pos' ? (
        <p className="muted walkin-hint">
          {posBusy
            ? 'De pinterminal is nu bezet met een andere betaling — het bedrag wordt na "Boeking bevestigen" alsnog naar de terminal gestuurd zodra die vrij is (of gebruik "Opnieuw proberen"). '
            : ''}
          Na "Boeking bevestigen" gaat het bedrag direct naar de pinterminal van
          deze locatie.{' '}
          {anonymous ? 'Er wordt geen ticket gemaild (geen e-mail).' : 'Het ticket wordt gemaild zodra de betaling binnen is.'}
          {checkin ? ' De klant wordt ingecheckt zodra de betaling binnen is.' : ''}
          {' '}Lukt de betaling niet, dan blijft de boeking staan als "Nog niet betaald".
        </p>
      ) : null}

      {paymentMethod === 'giftcard' ? (
        <p className="muted walkin-hint">
          Na "Boeking bevestigen" scan of typ je de cadeaubon. Dekt die niet alles,
          dan gaat de rest naar de pinterminal{posAvailable ? '' : ' (nu niet beschikbaar: de rest blijft dan "Nog niet betaald")'}.
          {anonymous ? ' Er wordt geen ticket gemaild (geen e-mail).' : ' Het ticket wordt gemaild zodra alles betaald is.'}
        </p>
      ) : null}

      {paymentMethod === 'unpaid' ? (
        <>
          <label className="walkin-warn-ack walkin-paylink-toggle">
            <input
              type="checkbox"
              checked={sendPaymentLink}
              onChange={(e) => onSendPaymentLink(e.target.checked)}
            />
            <span>Stuur betaallink mee in de bevestigingsmail</span>
          </label>
          <p className="muted walkin-hint">
            {sendPaymentLink
              ? 'De bevestigingsmail bevat het openstaande bedrag en een betaallink waarmee de klant online kan betalen. Later opnieuw versturen kan via "Stuur betaallink" op de boekingspagina.'
              : 'De klant betaalt bij aankomst aan de balie. Een betaallink alsnog versturen kan later via "Stuur betaallink" op de boekingspagina.'}
          </p>
        </>
      ) : null}

      {paymentMethod !== 'comp' ? (
        <label className="walkin-field">
          <span>Voucher / kortingscode (optioneel)</span>
          <div className="walkin-discount-row">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => onDiscountCode(e.target.value.toUpperCase())}
              onBlur={() => onDiscountCheck()}
              placeholder="WELCOME10"
            />
            <button
              type="button"
              className="btn-link"
              onClick={onDiscountCheck}
              disabled={discountChecking || discountCode.trim().length === 0}
            >
              {discountChecking ? '…' : 'Controleer'}
            </button>
          </div>
          {discountPreview ? (
            discountPreview.valid && discountPreview.amountOffCents != null ? (
              <span className="walkin-discount-ok">
                – €{(discountPreview.amountOffCents / 100).toFixed(2)} korting
                {' → totaal € '}
                {((amountCents - discountPreview.amountOffCents) / 100).toFixed(2)}
              </span>
            ) : (
              <span className="walkin-discount-bad">
                Code niet geldig: {discountPreview.reason ?? 'onbekend'}
              </span>
            )
          ) : null}
        </label>
      ) : null}

      <label className="walkin-field">
        <span>Notitie (optioneel)</span>
        <textarea
          value={notes}
          rows={2}
          onChange={(e) => onNotes(e.target.value)}
          placeholder="Bijv. tel. boeking, gast verjaardag, …"
        />
      </label>
    </div>
  );
}

// -----------------------------------------------------------------------------
// Success view — one row per departure
// -----------------------------------------------------------------------------
function SuccessView({
  bookingId, bookingNumber, departures, pos: posInitial, giftCard = false, posAvailable = false,
  checkin, anonymous, strictPos = false, panelRef, onClose,
}: {
  bookingId: string;
  bookingNumber: string;
  departures: WalkinDepartureResult[];
  pos: boolean;
  // Cadeaubon: spend a card first; a rest goes to the terminal when available.
  giftCard?: boolean;
  posAvailable?: boolean;
  checkin: boolean;
  anonymous: boolean;
  // Quick flow: a failed POS payment offers only retry or cancel — no
  // "Anders afrekenen" (no unpaid leftovers from failed payments).
  strictPos?: boolean;
  // Lets the modal shell free the reader before ✕ / Escape unmounts the panel.
  panelRef?: Ref<TerminalChargeHandle>;
  onClose: () => void;
}) {
  // POS: 'pending' while the terminal charge runs, then 'paid' / 'unpaid' /
  // 'cancelled' (operator chose "Boeking annuleren" - no ghost booking).
  const [pos, setPos] = useState(posInitial);
  const [posState, setPosState] = useState<'pending' | 'paid' | 'unpaid' | 'cancelled'>(posInitial ? 'pending' : 'paid');
  // Cadeaubon flow: 'apply' until the card is used, then 'paid' (card covered
  // it all), 'rest' (the rest went to the terminal) or 'unpaid' (rest open).
  const [gcState, setGcState] = useState<'apply' | 'paid' | 'rest' | 'unpaid' | null>(giftCard ? 'apply' : null);
  const [gcNote, setGcNote] = useState<string | null>(null);

  // The card covered the whole booking: no webhook follows, so mail the
  // tickets and check in from here.
  const finishCovered = async () => {
    const problems: string[] = [];
    for (const d of departures) {
      if (!anonymous) {
        const r = await resendBookingEmail({ booking_id: d.departure_id, action: 'confirmation' });
        if (!r.ok) problems.push(`mail ${d.departure_number}: ${r.error?.message ?? r.error?.error ?? 'mislukt'}`);
      }
      if (checkin) {
        const c = await checkinBookingById(d.departure_id);
        if (!c.ok) problems.push(`inchecken ${d.departure_number}: ${c.error?.message ?? c.error?.error ?? 'mislukt'}`);
      }
    }
    setGcNote(problems.length > 0 ? problems.join(' · ') : null);
    setGcState('paid');
  };
  // POS + "Direct inchecken": the server checks in when the payment settles;
  // the panel reports the outcome.
  const [posCheckedIn, setPosCheckedIn] = useState<boolean | null>(null);

  const allCheckedIn = !pos && checkin && departures.every((d) => d.checked_in === true);

  const emailLine = (d: WalkinDepartureResult) => {
    if (anonymous) return <span className="muted"> — geen e-mail (balie)</span>;
    if (giftCard && !pos) {
      return gcState === 'paid'
        ? <span className="walkin-success-email ok"> — ticket gemaild</span>
        : <span className="muted"> — ticketmail volgt na betaling</span>;
    }
    if (pos) {
      if (posState === 'cancelled') return <span className="walkin-success-email err"> — geannuleerd</span>;
      if (posState === 'paid') return <span className="walkin-success-email ok"> — ticket wordt gemaild</span>;
      if (posState === 'unpaid') {
        return (
          <span className="walkin-success-email err">
            {' '}— nog niet betaald: ticketmail volgt zodra betaald (of via "Bevestiging versturen")
          </span>
        );
      }
      return <span className="muted"> — ticketmail volgt na betaling</span>;
    }
    return (
      <span className={`walkin-success-email ${d.email_sent ? 'ok' : 'err'}`}>
        {' '}— {d.email_sent
          ? 'mail verstuurd'
          : `mail mislukt: ${d.email_error ?? 'onbekend'}`}
      </span>
    );
  };

  // "Direct inchecken" result per departure (non-POS: done at creation).
  const checkinLine = (d: WalkinDepartureResult) => {
    if (!checkin || pos) return null;
    if (d.checked_in === true) return <span className="walkin-success-email ok"> · ingecheckt ✓</span>;
    if (d.checked_in === false) {
      return (
        <span className="walkin-success-email err">
          {' '}· inchecken mislukt ({d.checkin_error ?? 'onbekend'}) — check in via Vandaag
        </span>
      );
    }
    return null;
  };

  return (
    <div className="walkin-success">
      <div className="walkin-success-title">
        {pos
          ? posState === 'paid'
            ? checkin && posCheckedIn ? 'Boeking aangemaakt, betaald en ingecheckt ✓' : 'Boeking aangemaakt en betaald ✓'
            : posState === 'unpaid'
              ? 'Boeking aangemaakt — nog niet betaald'
              : posState === 'cancelled'
                ? 'Boeking geannuleerd'
                : 'Boeking aangemaakt'
          : allCheckedIn
            ? 'Boeking aangemaakt en ingecheckt ✓'
            : 'Boeking aangemaakt'}
      </div>
      <div className="walkin-success-number">{bookingNumber}</div>
      <ul className="walkin-success-departures">
        {departures.map((d) => (
          <li key={d.departure_id}>
            <span className="walkin-success-dep-num">{d.departure_number}</span>
            <span className="muted">
              {' '}· {d.party_size}p
              {d.departure_start
                ? <> · {new Date(d.departure_start).toLocaleString('nl-NL', {
                    weekday: 'short', day: 'numeric', month: 'short',
                    hour: '2-digit', minute: '2-digit',
                  })}</>
                : null}
            </span>
            {emailLine(d)}
            {checkinLine(d)}
          </li>
        ))}
      </ul>

      {gcState === 'apply' ? (
        <GiftCardApplyPanel
          bookingId={bookingId}
          onApplied={(res) => {
            if (res.fully_paid) {
              void finishCovered();
            } else if (posAvailable) {
              setGcState('rest');
              setPos(true);
              setPosState('pending');
            } else {
              setGcState('unpaid');
            }
          }}
        />
      ) : null}
      {gcState === 'paid' ? (
        <p className={`walkin-hint walkin-success-email ${gcNote ? 'err' : 'ok'}`}>
          Volledig betaald met de cadeaubon ✓
          {!anonymous ? ' · ticket gemaild' : ''}
          {checkin ? ' · ingecheckt' : ''}
          {gcNote ? ` — let op: ${gcNote}` : ''}
        </p>
      ) : null}
      {gcState === 'unpaid' ? (
        <p className="muted walkin-hint">
          De cadeaubon is gebruikt; de rest staat open als "Nog niet betaald" (afrekenen via de
          boekingspagina: pinterminal, contant of betaallink).
        </p>
      ) : null}

      {pos && posState === 'pending' ? (
        <TerminalChargePanel
          ref={panelRef}
          // This booking was created moments ago to carry this payment: if the
          // payment is abandoned the POS sweeper may cancel it again.
          provisional
          bookingId={bookingId}
          bookingNumber={bookingNumber}
          hideAbandon={strictPos}
          checkinDepartureId={checkin ? (departures[0]?.departure_id ?? null) : null}
          onSettled={(outcome) => {
            setPosCheckedIn(outcome.checkedIn);
            setPosState('paid');
          }}
          onAbandon={() => setPosState('unpaid')}
          cancelDepartureId={departures[0]?.departure_id ?? null}
          onCancelledBooking={() => setPosState('cancelled')}
        />
      ) : null}
      {pos && posState === 'paid' && checkin ? (
        <p className={`walkin-hint ${posCheckedIn ? 'walkin-success-email ok' : 'walkin-success-email err'}`}>
          {posCheckedIn
            ? 'Ingecheckt ✓'
            : 'Inchecken na betaling is niet bevestigd — check in via Vandaag.'}
        </p>
      ) : null}
      {pos && posState === 'unpaid' ? (
        <p className="muted walkin-hint">
          De boeking staat op "Nog niet betaald". Afrekenen kan later via de
          boekingspagina: opnieuw op de pinterminal, contant (Markeer als betaald)
          of met een betaallink.
        </p>
      ) : null}

      {posState !== 'pending' && gcState !== 'apply' ? (
        <button className="walkin-primary-btn" onClick={onClose}>
          Sluiten
        </button>
      ) : null}
    </div>
  );
}
